import type { ReactNode, SyntheticEvent } from 'react';
import { forwardRef } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { FormHelperText, InputLabel, type AutocompleteRenderGetTagProps } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Checkbox } from './Checkbox';
import { StyledAutocomplete, StyledTextField, Wrapper } from './Autocomplete.styles';

export interface Option {
  label: string;
  value: string;
}

interface Props {
  label?: string;
  placeholder?: string;
  className?: string;
  background?: 'transparent' | 'white';
  readOnly?: boolean;
  options: Option[];
  multiple?: boolean;
  error?: boolean;
  helperText?: string;
  onChange: (value: unknown) => void;
  value: string | string[] | Option | Option[];
  disabled?: boolean;
  disableCloseOnSelect?: boolean;
  loading?: boolean;
  limitTags?: number;
  disableHelperText?: boolean;
  renderTags?: (value: (string | Option)[], getTagProps: AutocompleteRenderGetTagProps) => ReactNode;
}

const normalizeValue = (value: string | string[] | Option | Option[] | null, options: Option[]): Option | Option[] | null => {
  if (!value) return null;

  // Handle array values
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') {
        const option = options.find((opt) => opt.value === item);
        return option || { value: item, label: item };
      }
      return item;
    });
  }

  // Handle single value
  if (typeof value === 'string') {
    const option = options.find((opt) => opt.value === value);
    return option || { value, label: value };
  }

  return value;
};

export const Autocomplete = memo(
  forwardRef<HTMLInputElement, Props>(
    (
      {
        className,
        label,
        readOnly,
        options,
        onChange,
        placeholder,
        error,
        helperText,
        value,
        multiple = false,
        disableCloseOnSelect = multiple,
        loading = false,
        limitTags,
        disableHelperText = false,
        background,
        ...props
      },
      ref
    ) => {
      const normalizedValue = useMemo(() => normalizeValue(value, options), [value, options]);

      const handleOnChange = useCallback(
        (_: SyntheticEvent, newValue: unknown) => {
          onChange(newValue);
        },
        [onChange]
      );

      const showPopperOnTop = useMemo(() => Array.isArray(normalizedValue) && normalizedValue.length > 15, [normalizedValue]);

      return (
        <Wrapper>
          <InputLabel>{label}</InputLabel>
          <StyledAutocomplete
            popupIcon={<ExpandMore />}
            loading={loading}
            renderInput={(params) => (
              <StyledTextField {...params} error={error} placeholder={placeholder} variant="outlined" inputRef={ref} background={background} />
            )}
            renderOption={(optionProps, option, { selected }) => (
              <li {...optionProps} key={optionProps.id}>
                {multiple && <Checkbox style={{ marginRight: 8 }} checked={selected} />}
                {typeof option === 'string' ? option : option.label}
              </li>
            )}
            options={options}
            value={normalizedValue}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            isOptionEqualToValue={(option, optionValue) =>
              typeof option !== 'string' && typeof optionValue !== 'string' ? option.value === optionValue.value : option === optionValue
            }
            onChange={handleOnChange}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 13000,
                  position: 'absolute',
                  maxHeight: '300px',
                },
                placement: showPopperOnTop ? 'top-start' : 'bottom-start',
              },
            }}
            disableCloseOnSelect={disableCloseOnSelect}
            limitTags={limitTags}
            multiple={multiple}
            {...props}
          />
          {!disableHelperText && (
            <FormHelperText error={error} variant="filled">
              {helperText ?? ''}
            </FormHelperText>
          )}
        </Wrapper>
      );
    }
  )
);
